import * as React from 'react';
import { AlertTriangle, X } from 'lucide-react';

import { Button } from '../primitives/button';
import { Badge } from '../primitives/badge';
import {
  resolveFieldError,
  type FieldErrorIssue,
  type UseFieldErrorResult,
} from '../primitives/field-aria';
import { cn } from '../lib/utils';
import {
  buildOptimisticRollback,
  mapServerErrors,
  type OptimisticMutationOpts,
  type ServerErrorPayload,
  type SetFieldError,
  type UseOptimisticRollbackHelpers,
} from './form-archetype-core';

/**
 * Canonical create/edit form archetype contract.
 *
 * Every module form view should compose `useFormArchetype()` and render
 * `<FormArchetypeDuplicateBanner />` near the top of its `<FormShell>`.
 *
 * The hook + banner bundle the 3 affordances that the audit's
 * `partial 3/6 baseline` (from `FormShell` alone) does not provide:
 *
 *   4.  server-error-mapping  — `mapServerErrors(problem, setError)`
 *                                helper + the canonical `fieldErrors`
 *                                accessor consumed by `FormShell`'s
 *                                error summary.
 *   5.  duplicate-detection   — `useDuplicateCheck()` + `duplicateMatch`
 *                                state + `<DuplicateDetectionBanner />`
 *                                JSX.
 *   6.  optimistic-rollback   — `onMutate` snapshot + `onError` revert
 *                                helpers wired through the mutation
 *                                lifecycle. Consumers pass their own
 *                                `useApiMutation` instance and the hook
 *                                wraps it.
 *
 * The audit (`scripts/audit-form-archetype-coverage.mjs`) detects this
 * primitive by the testid prefix `form-archetype-`. Views that compose
 * the hook + banner automatically count toward the 3 advanced features
 * (server-error-mapping / duplicate-detection / optimistic-rollback)
 * without spreading their patterns inline.
 */

// ─── Server error mapping ─────────────────────────────────────────
//
// `mapServerErrors` and its payload types live in `form-archetype-core.ts`
// so the jest stub in `@tyndo/test-preset` can IMPORT them rather than
// reimplement them. Re-exported here to keep the public entry point of the
// archetype a single module.
export {
  mapServerErrors,
  type ServerErrorPayload,
  type SetFieldError,
} from './form-archetype-core';

// ─── Duplicate detection ──────────────────────────────────────────

export interface DuplicateCandidate {
  /** Stable id of the candidate record. */
  id: string;
  /** Short human label rendered as the banner row. */
  label: string;
  /** Optional secondary line (e.g. "Created 2 days ago by user@…"). */
  description?: string;
  /** Optional confidence in [0,1]; the banner renders a badge when set. */
  confidence?: number;
  /** Action a user can take. Defaults to "Compare" (no-op if unwired). */
  href?: string;
  onCompare?: () => void;
}

export interface UseDuplicateCheckOpts<TFormState> {
  /** Current form state — the hook re-evaluates on change. */
  formState: TFormState;
  /**
   * Async predicate. Return [] for no duplicates, an array of
   * candidates otherwise. Throwing or rejecting is treated as "no
   * duplicates found this tick".
   */
  check: (state: TFormState) => Promise<DuplicateCandidate[]>;
  /** Debounce duration in ms. Default 350. */
  debounceMs?: number;
  /** When false the hook never fires. Useful for "edit existing" mode. */
  enabled?: boolean;
}

export interface UseDuplicateCheckResult {
  /** Latest duplicate candidate list. Empty when none found. */
  duplicates: DuplicateCandidate[];
  /** True when there is at least one duplicate candidate. */
  isDuplicate: boolean;
  /** True while a check is in-flight. */
  checking: boolean;
  /** User dismissed the banner this session. */
  dismissed: boolean;
  /** Dismiss the banner (until form state changes). */
  dismiss: () => void;
}

/**
 * Debounced async duplicate-candidates probe. Renders nothing on its
 * own; pair with `<DuplicateDetectionBanner duplicates=… />`.
 */
export function useFormDuplicateCheck<TFormState>(
  opts: UseDuplicateCheckOpts<TFormState>,
): UseDuplicateCheckResult {
  const { formState, check, debounceMs = 350, enabled = true } = opts;
  const [duplicates, setDuplicates] = React.useState<DuplicateCandidate[]>([]);
  const [checking, setChecking] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);

  // Stable content key: callers frequently pass a fresh `formState` object
  // (and an inline `check`) every render. Depending on those by REFERENCE ran
  // these effects every render; the `setDuplicates([])` below allocates a new
  // array each time (never === prior state, so React never bails out) →
  // "Maximum update depth exceeded". Key the effects on a serialized snapshot,
  // and read the latest `check` from a ref so its identity isn't a dependency.
  const formKey = React.useMemo(() => {
    try {
      return JSON.stringify(formState);
    } catch {
      return String(formState);
    }
  }, [formState]);
  const checkRef = React.useRef(check);
  checkRef.current = check;

  // Reset dismiss when the form content changes.
  React.useEffect(() => {
    setDismissed(false);
  }, [formKey]);

  React.useEffect(() => {
    if (!enabled) {
      setDuplicates((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const handle = setTimeout(async () => {
      setChecking(true);
      try {
        const result = await checkRef.current(formState);
        setDuplicates(Array.isArray(result) ? result : []);
      } catch {
        setDuplicates([]);
      } finally {
        setChecking(false);
      }
    }, debounceMs);
    return () => clearTimeout(handle);
    // formState is read via the formKey-keyed closure; check via checkRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey, debounceMs, enabled]);

  return {
    duplicates,
    isDuplicate: duplicates.length > 0,
    checking,
    dismissed,
    dismiss: React.useCallback(() => setDismissed(true), []),
  };
}

// ─── Optimistic rollback ──────────────────────────────────────────
//
// The behaviour is `buildOptimisticRollback` in `form-archetype-core.ts`;
// the hook adds memoisation only. Splitting it that way is what lets the
// `@tyndo/test-preset` stub run the SHIPPED semantics instead of a
// look-alike that drifts.
export type {
  OptimisticMutationOpts,
  UseOptimisticRollbackHelpers,
} from './form-archetype-core';

/**
 * Wires the standard snapshot → optimistic update → rollback pattern.
 * Hooks into any mutation library (`useApiMutation`, react-query,
 * SWR-mutation) by passing the returned helpers into the lifecycle
 * callbacks the library supports.
 */
export function useOptimisticRollback<TData, TVars>(
  opts: OptimisticMutationOpts<TData, TVars>,
): UseOptimisticRollbackHelpers<TData, TVars> {
  const { snapshot, applyOptimistic, revert } = opts;
  // Rebuilt only when a callback identity changes; the helpers themselves
  // are the core factory's, so hook and stub cannot diverge in behaviour.
  return React.useMemo(
    () => buildOptimisticRollback<TData, TVars>({ snapshot, applyOptimistic, revert }),
    [snapshot, applyOptimistic, revert],
  );
}

// ─── Form archetype hook ──────────────────────────────────────────

export interface UseFormArchetypeOpts<TFormState> {
  /** Stable identifier used as the data-testid scope. */
  viewId: string;
  /** Current form state. Triggers re-checks. */
  formState: TFormState;
  /** Async duplicate probe. */
  checkDuplicates?: (state: TFormState) => Promise<DuplicateCandidate[]>;
  /** Disable duplicate checking (e.g. in edit-existing mode). */
  duplicateCheckEnabled?: boolean;
  /** Optional setError function for server-error mapping convenience. */
  setError?: SetFieldError;
  /**
   * Flat validation issues — the SAME array the view hands to `FormShell`'s
   * `errors` prop. When provided, `fieldError(name)` derives per-field
   * `aria-invalid` / `aria-describedby` wiring from it (WCAG 3.3.1).
   */
  issues?: ReadonlyArray<FieldErrorIssue>;
}

export interface UseFormArchetypeResult<TFormState> {
  /** Duplicate-check state for `<DuplicateDetectionBanner />`. */
  duplicates: UseDuplicateCheckResult;
  /**
   * Convenience: maps a server error payload to field-level errors via
   * the registered `setError`. Returns true if any field was set.
   */
  applyServerErrors: (problem: ServerErrorPayload | null | undefined) => boolean;
  /** Convenience: build the `data-testid` for an archetype affordance. */
  testId: (suffix: string) => string;
  /**
   * Per-field error association (WCAG 3.3.1), derived from `opts.issues`:
   *
   *   const nameErr = archetype.fieldError('name');
   *   <Input {...nameErr.fieldProps} />
   *   <FieldError id={nameErr.errorId} errors={nameErr.errors} />
   *
   * The generated ids are stable per archetype instance + field name. Views
   * composing the `<Field>` wrapper instead inherit the same wiring
   * automatically and don't need this helper.
   */
  fieldError: (field: string) => UseFieldErrorResult;
  /** The current form state (passed through for symmetry). */
  formState: TFormState;
}

/**
 * Compose-the-form archetype hook. Wraps `useDuplicateCheck` +
 * `mapServerErrors` + the testid helper. Consumers still pass their own
 * mutation hook; pair with `useOptimisticRollback` for the full
 * optimistic-rollback affordance.
 */
export function useFormArchetype<TFormState>(
  opts: UseFormArchetypeOpts<TFormState>,
): UseFormArchetypeResult<TFormState> {
  const { viewId, formState, checkDuplicates, duplicateCheckEnabled = true, setError, issues } = opts;

  const duplicates = useFormDuplicateCheck<TFormState>({
    formState,
    check: checkDuplicates ?? (async () => []),
    enabled: duplicateCheckEnabled && !!checkDuplicates,
  });

  const applyServerErrors = React.useCallback(
    (problem: ServerErrorPayload | null | undefined) => {
      if (!setError) return false;
      return mapServerErrors(problem, setError);
    },
    [setError],
  );

  const testId = React.useCallback(
    (suffix: string) => `form-archetype-${viewId}-${suffix}`,
    [viewId],
  );

  const ariaIdBase = React.useId();
  const fieldError = React.useCallback(
    (field: string) => resolveFieldError(issues, field, `${ariaIdBase}${viewId}`),
    [issues, ariaIdBase, viewId],
  );

  return { duplicates, applyServerErrors, testId, fieldError, formState };
}

// ─── DuplicateDetectionBanner JSX ─────────────────────────────────

export interface DuplicateDetectionBannerProps {
  duplicates: DuplicateCandidate[];
  dismissed?: boolean;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Yellow info banner shown above the form body when the duplicate probe
 * matches one or more existing records. Each match renders a "Compare"
 * action that the form's `onCompare` handler can wire to a master/detail
 * navigation.
 */
export function DuplicateDetectionBanner({
  duplicates,
  dismissed,
  onDismiss,
  className,
}: DuplicateDetectionBannerProps) {
  if (dismissed || duplicates.length === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="duplicate-detection-banner"
      className={cn(
        'rounded-md border border-[hsl(var(--alarm-minor))]/40 bg-[hsl(var(--alarm-minor))]/10 px-3 py-2',
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 text-[hsl(var(--alarm-minor))]" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[hsl(var(--alarm-minor))] text-sm">
            {duplicates.length === 1
              ? 'Possible duplicate detected'
              : `${duplicates.length} possible duplicates detected`}
          </div>
          <ul className="mt-1 space-y-1">
            {duplicates.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between gap-3 text-xs text-[hsl(var(--alarm-minor))]/90"
              >
                <div className="min-w-0">
                  <div className="truncate">
                    <span className="font-medium">{d.label}</span>
                    {typeof d.confidence === 'number' && (
                      <Badge
                        variant="warning"
                        className="ml-2 align-middle"
                      >
                        {Math.round(d.confidence * 100)}%
                      </Badge>
                    )}
                  </div>
                  {d.description && (
                    <div className="text-[hsl(var(--alarm-minor))]/70 truncate">
                      {d.description}
                    </div>
                  )}
                </div>
                {(d.onCompare || d.href) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={d.onCompare}
                    asChild={!!d.href}
                  >
                    {d.href ? <a href={d.href}>Compare</a> : 'Compare'}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
        {onDismiss && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onDismiss}
            aria-label="Dismiss duplicate banner"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
